// Playground bootstrap entry: wires DOM events and debug API.
// Runtime logic lives in modular files under `apps/playground/src/app/*`.
import { compileLuis, type LuisCompileResult } from '@lentystyle/compiler'
import { applyLuisToDocument } from '@lentystyle/runtime'
import { elements, initializeDefaultInputs, mutableState } from './app/state'
import { runApply, runApplyInternal, runCompile } from './app/compile'
import { applyHtml, handleLuisInput, setInputTab, updateLuisInputLineNumbers } from './app/ui'
import { syncLineNumberScroll } from './app/line-numbers'
import { updateLuisStats } from './app/render'
import { normalizePlaygroundLanguage, setPlaygroundLanguage } from './app/messages'

type DebugAPI = {
  compileLuis: typeof compileLuis
  applyLuisToDocument: typeof applyLuisToDocument
  setHtml: (html: string) => void
  run: (source?: string) => LuisCompileResult
  runAll: (source?: string, html?: string) => LuisCompileResult
}

declare global {
  interface Window {
    LentyStyleDebug: DebugAPI
  }
}

setPlaygroundLanguage('en')
initializeDefaultInputs()
updateLuisInputLineNumbers()
updateLuisStats(elements.luisInput.value)
bindEvents()
registerDebugApi()
applyHtml(false)
setInputTab('luis')
runApply()

// Bind all UI events for editor, tabs, and compile/apply actions.
function bindEvents(): void {
  elements.compileBtn.addEventListener('click', () => runCompile())
  elements.applyBtn.addEventListener('click', () => runApply())
  elements.applyHtmlBtn.addEventListener('click', () => applyHtml())
  elements.languageSelect.addEventListener('change', () => {
    setPlaygroundLanguage(normalizePlaygroundLanguage(elements.languageSelect.value))
    updateLuisStats(elements.luisInput.value)
    applyHtml(false)
    runApply()
  })

  elements.luisInput.addEventListener('beforeinput', (event) => {
    mutableState.pendingInputType = event.inputType
  })
  elements.luisInput.addEventListener('input', () => handleLuisInput())
  elements.luisInput.addEventListener('scroll', () => syncLineNumberScroll(elements.luisInput, elements.luisLineNumbers))

  elements.htmlInput.addEventListener('input', () => applyHtml(false))
  elements.cssOutput.addEventListener('scroll', () => syncLineNumberScroll(elements.cssOutput, elements.cssLineNumbers))

  elements.inputTabLuis.addEventListener('click', () => setInputTab('luis'))
  elements.inputTabHtml.addEventListener('click', () => setInputTab('html'))
}

// Register browser console debug helpers.
function registerDebugApi(): void {
  window.LentyStyleDebug = {
    compileLuis,
    applyLuisToDocument,
    setHtml: (html: string) => {
      elements.htmlInput.value = html
      applyHtml(false)
    },
    run: (source?: string) => runCompile(true, source),
    runAll: (source?: string, html?: string) => {
      if (typeof html === 'string') {
        elements.htmlInput.value = html
      }
      applyHtml(false)
      return runApplyInternal(source)
    },
  }
}
